import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import useGoogleStorageHelpers from 'utils/helpers/googleStorage.helpers';
import Dialog from 'components/Dialog';
import { StyledDataGrid } from 'components/StyledDataGrid/StyledDataGrid';
import theme from 'App.theme';
import { useLanguage } from 'context/AppContext';
import { ManualRow } from 'hooks/useSetManuals';

type Props = {
  open: boolean;
  close: () => void;
  manualRows: ManualRow[];
  disableVirtualization?: boolean;
};

export const SetManualsModal: FC<Props> = ({ open, close, manualRows, disableVirtualization = false }) => {
  const lang = useLanguage()[0].SetManualsModal;

  const { getDownloadUrlFromGS } = useGoogleStorageHelpers();

  const [disabledLinks, setDisabledLinks] = useState<Record<string, true>>({});

  const handleViewManual = useCallback(
    async (manualRow: ManualRow) => {
      if (!manualRow.path) return;

      setDisabledLinks(prev => ({ ...prev, [manualRow.id]: true }));

      try {
        const url = await getDownloadUrlFromGS(manualRow.path);
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error('Failed to get manual download URL', error);
      } finally {
        setDisabledLinks(prev => {
          const { [manualRow.id]: _, ...rest } = prev;
          return rest;
        });
      }
    },
    [getDownloadUrlFromGS],
  );

  const memoizedColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'type',
        headerName: lang.tableHeaders.type,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'code',
        headerName: lang.tableHeaders.code,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'name',
        headerName: lang.tableHeaders.name,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'path',
        headerName: lang.tableHeaders.manual,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }) => (
          <Link
            sx={{
              cursor: disabledLinks[row.id] ? 'not-allowed' : 'pointer',
              color: disabledLinks[row.id] ? theme.greys.GREY3 : theme.colors.primary,
              textDecoration: disabledLinks[row.id] ? 'none' : 'underline',
            }}
            onClick={() => handleViewManual(row)}
          >
            {lang.view}
          </Link>
        ),
      },
    ],
    [
      lang.tableHeaders.type,
      lang.tableHeaders.code,
      lang.tableHeaders.name,
      lang.tableHeaders.manual,
      lang.view,
      disabledLinks,
      handleViewManual,
    ],
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      title={lang.title}
      maxWidth="sm"
      contentSx={{ pb: 0, mb: theme.layout.spacing.medium }}
    >
      <Box sx={{ maxHeight: 360, width: '100%' }}>
        {manualRows.length > 0 ? (
          <StyledDataGrid
            hidePagination
            disableColumnMenu
            columns={memoizedColumns}
            rows={manualRows}
            rowHeight={theme.sizes.rowHeightCompact}
            getRowId={row => row.id}
            disableVirtualization={disableVirtualization}
          />
        ) : (
          <Typography>{lang.noManualsAvailable}</Typography>
        )}
      </Box>
    </Dialog>
  );
};
